/* ============================================================
   ProtoArc GA4 Tracker Module (assets/ga4-tracker.js)
   Version: 1.4 (JS-only) | GA4: G-KNWHVVXYJ6
   ============================================================ */

(function () {
  // ---- 配置 ----
  const CONFIG = {
    GA_ID: 'G-KNWHVVXYJ6',
    DEBUG: false,                          // 需要调试时改为 true
    SCROLL_THRESHOLDS: [25, 50, 75, 100],  // 滚动触发点
    HOVER_MIN_TIME: 500,                   // 图片悬停阈值(ms)
    SELECTORS: {
      buttons: 'button, .btn, .cta-button',
      links: 'a[href]',
      images: 'img',
      titles: 'h1, h2, h3',
      texts:  '.description, p'
    }
  };

  // ---- 轻量日志 ----
  const log = (...args) => CONFIG.DEBUG && console.log('[GA4Tracker]', ...args);

  // ---- 保证 GA4 可用（若未加载则动态加载）----
  function ensureGA() {
    // 若已有 gtag，直接返回
    if (window.gtag && window.dataLayer) return Promise.resolve();

    // 建立 dataLayer 与 gtag 代理（可缓存事件，等真脚本加载后发送）
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function(){ dataLayer.push(arguments); };

    // 若已存在同源脚本标签，认为会加载完成
    const exists = !!document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    if (exists) return Promise.resolve();

    // 动态加载 gtag.js
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(CONFIG.GA_ID)}`;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // ---- 初始化 GA4 config ----
  function initGAConfig() {
    window.gtag('js', new Date());
    window.gtag('config', CONFIG.GA_ID);
    log('GA configured with', CONFIG.GA_ID);
  }

  // ---- 主追踪器 ----
  const Tracker = {
    pageName: null,
    scrollReached: {},
    hoverTimers: new WeakMap(),

    init() {
      // 页面名优先取 <body data-page-name="...">，否则用 document.title 或 pathname
      const body = document.body;
      this.pageName =
        (body && body.dataset && body.dataset.pageName) ||
        document.title ||
        window.location.pathname;

      this._trackScroll();
      this._trackClicks();
      this._trackHover();
      log('Initialized for', this.pageName);
    },

    // 滚动阈值追踪
    _trackScroll() {
      CONFIG.SCROLL_THRESHOLDS.forEach(p => this.scrollReached[p] = false);
      window.addEventListener('scroll', () => {
        const scrollTop = window.scrollY;
        const winH = window.innerHeight;
        const docH = document.documentElement.scrollHeight || document.body.scrollHeight || 1;
        const percent = Math.round(((scrollTop + winH) / docH) * 100);

        CONFIG.SCROLL_THRESHOLDS.forEach(p => {
          if (!this.scrollReached[p] && percent >= p) {
            this.scrollReached[p] = true;
            this._send('scroll_progress', {
              page_name: this.pageName,
              scroll_percent: p
            });
            log(`Scroll reached ${p}%`);
          }
        });
      }, { passive: true });
    },

    // 点击追踪（事件委托）
    _trackClicks() {
      document.addEventListener('click', (e) => {
        const target = e.target.closest(
          `${CONFIG.SELECTORS.buttons}, ${CONFIG.SELECTORS.links}, ${CONFIG.SELECTORS.images}, ${CONFIG.SELECTORS.titles}, ${CONFIG.SELECTORS.texts}`
        );
        if (!target) return;

        // 按钮
        if (target.matches(CONFIG.SELECTORS.buttons)) {
          this._send('button_click', {
            page_name: this.pageName,
            element_type: 'button',
            button_text: (target.innerText || '').trim(),
            button_id: target.id || null
          });
          return;
        }

        // 链接（区分外链 / 锚点）
        if (target.matches(CONFIG.SELECTORS.links)) {
          const isExternal = target.host && target.host !== location.host;
          this._send('link_click', {
            page_name: this.pageName,
            element_type: 'link',
            link_url: target.href,
            link_type: isExternal ? 'external' : 'anchor',
            link_text: (target.innerText || '').trim()
          });
          return;
        }

        // 图片点击
        if (target.matches(CONFIG.SELECTORS.images)) {
          this._send('image_click', {
            page_name: this.pageName,
            element_type: 'image',
            image_alt: target.alt || '(no alt)',
            image_src: target.src || null
          });
          return;
        }

        // 标题点击
        if (target.matches(CONFIG.SELECTORS.titles)) {
          this._send('title_click', {
            page_name: this.pageName,
            element_type: 'title',
            title_text: (target.innerText || '').trim()
          });
          return;
        }

        // 段落/文本点击
        if (target.matches(CONFIG.SELECTORS.texts)) {
          const preview = ((target.innerText || '').trim()).slice(0, 30) + '...';
          this._send('paragraph_click', {
            page_name: this.pageName,
            element_type: 'text',
            text_preview: preview
          });
        }
      });
    },

    // 图片悬停 >= 阈值(ms)
    _trackHover() {
      document.querySelectorAll(CONFIG.SELECTORS.images).forEach(img => {
        img.addEventListener('mouseenter', () => {
          this.hoverTimers.set(img, Date.now());
        });
        img.addEventListener('mouseleave', () => {
          const start = this.hoverTimers.get(img);
          if (!start) return;
          const duration = Date.now() - start;
          if (duration >= CONFIG.HOVER_MIN_TIME) {
            this._send('image_hover', {
              page_name: this.pageName,
              element_type: 'image',
              hover_time: duration,
              image_alt: img.alt || '(no alt)'
            });
            log('Image hover', img.alt || '', duration + 'ms');
          }
          this.hoverTimers.delete(img);
        });
      });
    },

    // 统一发送
    _send(name, params = {}) {
      if (typeof window.gtag !== 'function') {
        log('⚠️ gtag not ready, skipped:', name, params);
        return;
      }
      window.gtag('event', name, params);
      log('Sent event:', name, params);
    }
  };

  // ---- 启动流程：保证 GA 可用 → 配置 → DOM 就绪后绑定 ----
  ensureGA()
    .catch(() => { /* 脚本加载失败也不阻塞后续渲染 */ })
    .finally(() => {
      initGAConfig();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => Tracker.init());
      } else {
        Tracker.init();
      }
    });
})();
